// src/components/pages/HomePage.js
import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTranslations } from '../../languages';
import { trackEvent } from '../../services/analyticsService';
import ContactForm from '../common/ContactForm';

function HomePage({ section = "home" }) {
  // Get current language and translations
  const { language } = useLanguage();
  const translations = getTranslations(language);

  // Refs für jeden Abschnitt
  const homeRef = useRef(null);
  const servicesRef = useRef(null);
  const portfolioRef = useRef(null);
  const aboutRef = useRef(null);
  const teamRef = useRef(null);
  const contactRef = useRef(null);

  // Force scroll to top on component mount
  useEffect(() => {
    // Immediate scroll to top
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Scroll zum entsprechenden Abschnitt beim Laden oder Ändern der section
  useEffect(() => {
    const sectionRefs = {
      home: homeRef,
      services: servicesRef,
      portfolio: portfolioRef,
      about: aboutRef,
      team: teamRef,
      contact: contactRef
    };

    // Wenn "home" ausgewählt ist oder kein Abschnitt gefunden wurde, scrolle zum absoluten Seitenanfang
    if (section === "home" || !sectionRefs[section] || !sectionRefs[section].current) {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      return;
    }

    // Für alle anderen Abschnitte, scrolle zum entsprechenden Abschnitt
    const targetRef = sectionRefs[section];
    // Kurze Verzögerung, um sicherzustellen, dass die Komponente vollständig gerendert ist
    const timer = setTimeout(() => {
      targetRef.current.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    
    return () => clearTimeout(timer);
  }, [section]);

  // Initialize scroll reveal animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const handleIntersection = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);

    // Observe all elements with scroll reveal classes
    const elementsToObserve = document.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale, .stagger-reveal');
    elementsToObserve.forEach(el => observer.observe(el));

    return () => {
      elementsToObserve.forEach(el => observer.unobserve(el));
    };
  }, []);

  // Load portfolio and team images if they exist
  useEffect(() => {
    const loadImages = () => {
      // Portfolio image mappings
      const portfolioImages = [
        { selector: '.preview-1', src: '/images/portfolio/gluecks-couch.jpg' },
        { selector: '.preview-2', src: '/images/portfolio/hamburger-perspektivwechsel.jpg' }
      ];

      portfolioImages.forEach(({ selector, src }) => {
        const element = document.querySelector(selector);
        if (element) {
          const img = new Image();
          img.onload = () => {
            // Image loaded successfully
            element.style.backgroundImage = `url('${src}')`;
            element.classList.add('has-image');
          };
          img.onerror = () => {
            // Image failed to load, keep the styled placeholder
            console.log(`Portfolio image not found: ${src}`);
          };
          img.src = src;
        }
      });

      // Team image for Claude
      const claudeTeamPhoto = document.querySelector('.team-photo-4');
      if (claudeTeamPhoto) {
        const claudeImg = new Image();
        claudeImg.onload = () => {
          // Use the official Claude icon if available
          claudeTeamPhoto.style.backgroundImage = `url('/images/team/claude-icon.png')`;
          claudeTeamPhoto.style.backgroundSize = 'cover';
        };
        claudeImg.onerror = () => {
          // Keep the default "C" icon
          console.log('Claude team image not found, using default icon');
        };
        claudeImg.src = '/images/team/claude-icon.png';
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(loadImages, 100);
    return () => clearTimeout(timer);
  }, []);

  // Update document title and meta tags when language changes
  useEffect(() => {
    document.title = translations.meta.title;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', translations.meta.description);
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', translations.meta.keywords);
    }
    
    // Update OpenGraph meta tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    
    if (ogTitle) {
      ogTitle.setAttribute('content', translations.meta.title);
    }
    
    if (ogDescription) {
      ogDescription.setAttribute('content', translations.meta.description);
    }
    
    // Update Twitter meta tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    
    if (twitterTitle) {
      twitterTitle.setAttribute('content', translations.meta.title);
    }
    
    if (twitterDescription) {
      twitterDescription.setAttribute('content', translations.meta.description);
    }
  }, [language, translations]);

  // Event-Tracking-Funktionen
  const handleButtonClick = (buttonName) => {
    trackEvent('Engagement', 'Button Click', buttonName);
  };

  const handleServiceClick = (serviceName) => {
    trackEvent('Services', 'Service View', serviceName);
  };

  const handlePortfolioClick = (projectName) => {
    trackEvent('Portfolio', 'Project View', projectName);
  };

  const handleSocialClick = (memberName, platform) => {
    trackEvent('Team', 'Social Link Click', `${memberName} - ${platform}`);
  };

  const handleStatsClick = (statName) => {
    trackEvent('About', 'Stats Click', statName);
  };

  return (
    <>
      {/* Home Section */}
      <section id="home" className="hero" ref={homeRef}>
        <div className="lucent-rays"></div>
        <div className="container">
          <h1>{translations.hero.title}</h1>
          <p>{translations.hero.subtitle}</p>
          <div className="hero-buttons">
            <button 
              className="cta-button"
              onClick={() => {
                handleButtonClick('Hero CTA');
                contactRef.current?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {translations.hero.cta}
            </button>
            <button 
              className="secondary-button hero-secondary"
              onClick={() => {
                handleButtonClick('View Portfolio');
                portfolioRef.current?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {translations.hero.secondary || 'View Our Work'}
            </button>
          </div>
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">3</span>
              <span className="stat-label">{language === 'de' ? 'Zufriedene Kunden' : 'Happy Clients'}</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">100%</span>
              <span className="stat-label">{language === 'de' ? 'Maßgeschneidert' : 'Custom Design'}</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">∞</span>
              <span className="stat-label">{language === 'de' ? 'Kreative Ideen' : 'Creative Ideas'}</span>
            </div>
          </div>
        </div>
      </section>
      
      {/* Services Section */}
      <section id="services" className="services-preview" ref={servicesRef}>
        <div className="container">
          <h2>{translations.services.title}</h2>
          <div className="services-grid">
            <div 
              className="service-card scroll-reveal delay-1"
              onClick={() => handleServiceClick(translations.services.webDesign.title)}
            >
              <div className="service-icon">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>{translations.services.webDesign.title}</h3>
              <p>{translations.services.webDesign.description}</p>
              <span className="service-arrow">→</span>
            </div>
            <div 
              className="service-card scroll-reveal delay-2"
              onClick={() => handleServiceClick(translations.services.development.title)}
            >
              <div className="service-icon">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 18L22 12L16 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 6L2 12L8 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>{translations.services.development.title}</h3>
              <p>{translations.services.development.description}</p>
              <span className="service-arrow">→</span>
            </div>
            <div 
              className="service-card scroll-reveal delay-3"
              onClick={() => handleServiceClick(translations.services.seo.title)}
            >
              <div className="service-icon">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>{translations.services.seo.title}</h3>
              <p>{translations.services.seo.description}</p>
              <span className="service-arrow">→</span>
            </div>
          </div>
        </div>
      </section>
      
      {/* Portfolio Section */}
      <section id="portfolio" className="portfolio-section" ref={portfolioRef}>
        <div className="container">
          <h2>{translations.portfolio.title}</h2>
          <p>{translations.portfolio.subtitle}</p>
          
          <div className="portfolio-grid">
            {/* Portfolio Item 1 */}
            <div 
              className="portfolio-item scroll-reveal-scale delay-1"
              onClick={() => handlePortfolioClick(translations.portfolio.projects.project1.title)}
            >
              <div className="portfolio-image">
                <div className="portfolio-preview preview-1"></div>
              </div>
              <div className="portfolio-info">
                <h3>{translations.portfolio.projects.project1.title}</h3>
                <p>{translations.portfolio.projects.project1.description}</p>
                {translations.portfolio.projects.project1.tags.map((tag, index) => (
                  <span key={index} className="portfolio-tag">{tag}</span>
                ))}
              </div>
            </div>
            
            {/* Portfolio Item 2 */}
            <div 
              className="portfolio-item scroll-reveal-scale delay-2"
              onClick={() => handlePortfolioClick(translations.portfolio.projects.project2.title)}
            >
              <div className="portfolio-image">
                <div className="portfolio-preview preview-2"></div>
              </div>
              <div className="portfolio-info">
                <h3>{translations.portfolio.projects.project2.title}</h3>
                <p>{translations.portfolio.projects.project2.description}</p>
                {translations.portfolio.projects.project2.tags.map((tag, index) => (
                  <span key={index} className="portfolio-tag">{tag}</span>
                ))}
              </div>
            </div>
            
            {/* Portfolio Item 3 */}
            <div 
              className="portfolio-item scroll-reveal-scale delay-3"
              onClick={() => handlePortfolioClick('TechStartup Pro')}
            >
              <div className="portfolio-image">
                <div className="portfolio-preview preview-3"></div>
              </div>
              <div className="portfolio-info">
                <h3>TechStartup Pro</h3>
                <p>{language === 'de' ? 'Moderne SaaS-Plattform mit Dashboard und Analysen für Tech-Unternehmen.' : 'Modern SaaS platform with dashboard and analytics for tech companies.'}</p>
                <span className="portfolio-tag">React</span>
                <span className="portfolio-tag">Node.js</span>
                <span className="portfolio-tag">Analytics</span>
              </div>
            </div>
            
            {/* Portfolio Item 4 */}
            <div 
              className="portfolio-item scroll-reveal-scale delay-4"
              onClick={() => handlePortfolioClick('Fashion Brand X')}
            >
              <div className="portfolio-image">
                <div className="portfolio-preview preview-4"></div>
              </div>
              <div className="portfolio-info">
                <h3>Fashion Brand X</h3>
                <p>{language === 'de' ? 'Luxuriöser E-Commerce-Shop mit AR-Try-On-Funktion und personalisiertem Shopping.' : 'Luxury e-commerce store with AR try-on feature and personalized shopping.'}</p>
                <span className="portfolio-tag">E-Commerce</span>
                <span className="portfolio-tag">AR/VR</span>
                <span className="portfolio-tag">Mobile-First</span>
              </div>
            </div>
            
            {/* View More Button */}
            <div className="portfolio-view-more">
              <button 
                className="secondary-button"
                onClick={() => handleButtonClick('View All Projects')}
              >
                {translations.portfolio.viewAll}
              </button>
            </div>
          </div>
        </div>
      </section>
      
      {/* About Section */}
      <section id="about" className="about-section" ref={aboutRef}>
        <div className="container">
          <h2>{translations.about.title}</h2>
          <p>{translations.about.subtitle}</p>
          <div className="about-content">
            <div className="about-text">
              <p>{translations.about.paragraph1}</p>
              <p>{translations.about.paragraph2}</p>
            </div>
            <div className="about-values">
              <div 
                className="value-item stagger-reveal delay-1"
                onClick={() => handleStatsClick('Quality First')}
              >
                <h3 className="value-title">{translations.about.values.quality.title}</h3>
                <p className="value-description">{translations.about.values.quality.description}</p>
              </div>
              <div 
                className="value-item stagger-reveal delay-2"
                onClick={() => handleStatsClick('True Partnership')}
              >
                <h3 className="value-title">{translations.about.values.partnership.title}</h3>
                <p className="value-description">{translations.about.values.partnership.description}</p>
              </div>
              <div 
                className="value-item stagger-reveal delay-3"
                onClick={() => handleStatsClick('Innovation')}
              >
                <h3 className="value-title">{translations.about.values.innovation.title}</h3>
                <p className="value-description">{translations.about.values.innovation.description}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Process Section */}
      <section className="process-section">
        <div className="container">
          <h2>{language === 'de' ? 'Unser Bewährter Prozess' : 'Our Proven Process'}</h2>
          <p className="process-subtitle">
            {language === 'de' 
              ? 'Von der Idee zur Realität in vier transparenten Schritten' 
              : 'From concept to reality in four transparent steps'}
          </p>
          <div className="process-timeline">
            <div className="process-step">
              <div className="step-number">01</div>
              <h3>{language === 'de' ? 'Entdecken' : 'Discover'}</h3>
              <p>{language === 'de' 
                ? 'Wir hören zu, verstehen Ihre Vision und analysieren Ihre Ziele und Zielgruppe.'
                : 'We listen, understand your vision, and analyze your goals and target audience.'}
              </p>
            </div>
            <div className="process-step">
              <div className="step-number">02</div>
              <h3>{language === 'de' ? 'Gestalten' : 'Design'}</h3>
              <p>{language === 'de'
                ? 'Kreative Konzepte und Prototypen, die Ihre Marke perfekt repräsentieren.'
                : 'Creative concepts and prototypes that perfectly represent your brand.'}
              </p>
            </div>
            <div className="process-step">
              <div className="step-number">03</div>
              <h3>{language === 'de' ? 'Entwickeln' : 'Develop'}</h3>
              <p>{language === 'de'
                ? 'Sauberer Code und modernste Technologien für optimale Performance.'
                : 'Clean code and cutting-edge technologies for optimal performance.'}
              </p>
            </div>
            <div className="process-step">
              <div className="step-number">04</div>
              <h3>{language === 'de' ? 'Launchen' : 'Launch'}</h3>
              <p>{language === 'de'
                ? 'Nahtloser Go-Live und kontinuierliche Unterstützung für Ihren Erfolg.'
                : 'Seamless go-live and ongoing support for your success.'}
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Technology Section */}
      <section className="technology-section">
        <div className="container">
          <h2>{translations.technology.title}</h2>
          <p className="technology-subtitle">{translations.technology.subtitle}</p>
          <div className="technology-grid">
            <div className="tech-card">
              <div className="tech-icon">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7V12C2 16.5 4.23 20.68 7.62 21.97L12 24L16.38 21.97C19.77 20.68 22 16.5 22 12V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 12L8 8M12 12L16 8M12 12V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>{translations.technology.items.modern.title}</h3>
              <p>{translations.technology.items.modern.description}</p>
            </div>
            <div className="tech-card">
              <div className="tech-icon">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 16V12L8 8L4 12V16H8L12 20L16 16H20V12L16 8L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h3>{translations.technology.items.ai.title}</h3>
              <p>{translations.technology.items.ai.description}</p>
            </div>
            <div className="tech-card">
              <div className="tech-icon">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L3 7V11C3 16 6.5 20.16 11.24 21.96L12 22L12.76 21.97C17.5 20.16 21 16 21 11V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>{translations.technology.items.security.title}</h3>
              <p>{translations.technology.items.security.description}</p>
            </div>
            <div className="tech-card">
              <div className="tech-icon">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>{translations.technology.items.performance.title}</h3>
              <p>{translations.technology.items.performance.description}</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Team Section */}
      <section id="team" className="team-section" ref={teamRef}>
        <div className="container">
          <h2>{translations.team.title}</h2>
          <p>{translations.team.subtitle}</p>
          <div className="team-badges">
            <span className="badge">{language === 'de' ? 'Boutique Studio' : 'Boutique Studio'}</span>
            <span className="badge">{language === 'de' ? 'Persönliche Betreuung' : 'Personal Attention'}</span>
            <span className="badge">{language === 'de' ? 'Maßgeschneiderte Lösungen' : 'Custom Solutions'}</span>
          </div>
          
          <div className="team-grid">
            {/* Team Member 1 */}
            <div className="team-member">
              <div className="team-photo team-photo-1"></div>
              <h3>{translations.team.members.member1.name}</h3>
              <p className="team-role">{translations.team.members.member1.role}</p>
              <p className="team-bio">{translations.team.members.member1.bio}</p>
              <div className="team-social">
                <a 
                  href="#" 
                  className="social-link" 
                  onClick={(e) => {
                    e.preventDefault();
                    handleSocialClick(translations.team.members.member1.name, 'LinkedIn');
                  }}
                >
                  LinkedIn
                </a>
                <a 
                  href="#" 
                  className="social-link" 
                  onClick={(e) => {
                    e.preventDefault();
                    handleSocialClick(translations.team.members.member1.name, 'Twitter');
                  }}
                >
                  Twitter
                </a>
              </div>
            </div>
            
            {/* Team Member 2 */}
            <div className="team-member">
              <div className="team-photo team-photo-2"></div>
              <h3>{translations.team.members.member2.name}</h3>
              <p className="team-role">{translations.team.members.member2.role}</p>
              <p className="team-bio">{translations.team.members.member2.bio}</p>
              <div className="team-social">
                <a 
                  href="#" 
                  className="social-link" 
                  onClick={(e) => {
                    e.preventDefault();
                    handleSocialClick(translations.team.members.member2.name, 'LinkedIn');
                  }}
                >
                  LinkedIn
                </a>
                <a 
                  href="#" 
                  className="social-link" 
                  onClick={(e) => {
                    e.preventDefault();
                    handleSocialClick(translations.team.members.member2.name, 'GitHub');
                  }}
                >
                  GitHub
                </a>
              </div>
            </div>
            
            {/* Team Member 3 */}
            <div className="team-member">
              <div className="team-photo team-photo-3"></div>
              <h3>{translations.team.members.member3.name}</h3>
              <p className="team-role">{translations.team.members.member3.role}</p>
              <p className="team-bio">{translations.team.members.member3.bio}</p>
              <div className="team-social">
                <a 
                  href="#" 
                  className="social-link" 
                  onClick={(e) => {
                    e.preventDefault();
                    handleSocialClick(translations.team.members.member3.name, 'LinkedIn');
                  }}
                >
                  LinkedIn
                </a>
                <a 
                  href="#" 
                  className="social-link" 
                  onClick={(e) => {
                    e.preventDefault();
                    handleSocialClick(translations.team.members.member3.name, 'Dribbble');
                  }}
                >
                  Dribbble
                </a>
              </div>
            </div>
            
            {/* Team Member 4 */}
            <div className="team-member">
              <div className="team-photo team-photo-4"></div>
              <h3>{translations.team.members.member4.name}</h3>
              <p className="team-role">{translations.team.members.member4.role}</p>
              <p className="team-bio">{translations.team.members.member4.bio}</p>
              <div className="team-social">
                <a 
                  href="#" 
                  className="social-link" 
                  onClick={(e) => {
                    e.preventDefault();
                    handleSocialClick(translations.team.members.member4.name, 'GitHub');
                  }}
                >
                  GitHub
                </a>
                <a 
                  href="#" 
                  className="social-link" 
                  onClick={(e) => {
                    e.preventDefault();
                    handleSocialClick(translations.team.members.member4.name, 'Claude AI');
                  }}
                >
                  Claude AI
                </a>
              </div>
            </div>
            
          </div>
        </div>
      </section>
      
      {/* Contact Section */}
      <section id="contact" className="contact-section" ref={contactRef}>
        <div className="container">
          <h2>{translations.contact.title}</h2>
          <p className="contact-subtitle">{translations.contact.subtitle}</p>
          <ContactForm />
        </div>
      </section>
    </>
  );
}

export default HomePage;